import QueryDB from "@/api/queryDB/common";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import {
  Box,
  Flex,
  TextField,
  Text,
  Button,
  Spinner,
  Heading,
} from "@radix-ui/themes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import i18next from "i18next";
import { FC, useEffect, useState } from "react";
import { refreshUser } from "@/utils/sharedHelperFunctions";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "@/routes/siteRoutesList";
import { Profile } from "@/models/database/profiles/profile.model";
import { createToast } from "@/utils/toaster/createToast";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../../types/authenticationTypes";
import { toast } from "react-toastify";

const QuickAccountCredentials: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const user = useCurrentUserProfile((s) => s.user);
  const setUser = useCurrentUserProfile((s) => s.setUser);

  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  const [values, setValues] = useState<Profile>({
    ...user,
    userName: "",
    email: "",
  });

  const setNewValue = (targetName: string, value: string) => {
    return setValues((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  const baseAuthenticationProps = {
    method: "email",
    emailAddress: values.email,
    platform: brand.brandValue,
    tenantId: brand.tenantId,
  } as AuthenticationProps;

  useEffect(() => {
    if (response?.status !== "verification-code-resent" && _error) {
      createToast({
        type: "error",
        message: _error?.message,
      });
    }

    if (response && response?.status === "verification-code-resent") {
      createToast({
        type: "success",
        message: `A verification email has been sent to ${values.email}`,
      });

      createToast({
        type: "success",
        message: "Profile updated successfully!",
      });
      navigate(siteRoutes["profile"]);
    }
  }, [response, _error]);

  const handleSendEmail = async () => {
    await authenticate({
      ...baseAuthenticationProps,
      action: "convert-to-permanent",
      autoVerify: true,
      siteDomain: brand.site,
    }).catch(() => {
      return toast.error("Email is not available, please choose another.");
    });
  };

  const saveChanges = async () => {
    // Remove try-catch from validation, add it around the actual async operations
    if (!values.userName || !values.email) {
      return createToast({
        type: "error",
        message: "Please fill in all fields.",
      });
    }
    if (values.userName.length < 3) {
      return createToast({
        type: "error",
        message: "Username must be at least 3 characters long.",
      });
    }
    if (!values.email.includes("@")) {
      return createToast({
        type: "error",
        message: "Please enter a valid email address.",
      });
    }

    try {
      await QueryDB.updateProfileDoc("profiles", user.uid, brand.tenantId, {
        ...values,
      });

      await refreshUser(setUser, user);

      await handleSendEmail();
    } catch (error) {
      console.error("Error updating profile:", error);
      createToast({
        type: "error",
        message: "An error occurred while updating your profile.",
      });
    }
  };

  return (
    <Box>
      <Heading as="h1" size="5">
        Let's set up your account
      </Heading>
      <Flex direction="column" gap="4">
        {/* Username */}
        <Flex direction="column" gap="1">
          <Text as="label" size="2">
            {i18next.t("auth.create.profile.input.username.label")}
          </Text>
          <TextField.Root
            placeholder={i18next.t(
              "auth.create.profile.input.username.placeholder"
            )}
            size="3"
            type="text"
            value={values.userName}
            onChange={(e) => {
              setNewValue("userName", e.target.value);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (values.userName !== "" && values.userName !== "") {
                  saveChanges();
                }
              }
            }}
          />
        </Flex>

        {/* Email */}
        <Flex direction="column" gap="1">
          <Text as="label" size="2">
            {i18next.t("auth.input.email.label")}
          </Text>
          <TextField.Root
            placeholder={i18next.t("auth.input.email.placeholder")}
            size="3"
            type="text"
            value={values.email}
            onChange={(e) => {
              setNewValue("email", e.target.value);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (values.userName !== "" && values.userName !== "") {
                  saveChanges();
                }
              }
            }}
          />
        </Flex>

        <Flex direction="column" gap="3">
          <Button
            variant="solid"
            size="3"
            onClick={async (e) => {
              saveChanges();
            }}
          >
            Save changes
          </Button>
          <Button
            variant="soft"
            size="3"
            onClick={async (e) => {
              navigate(siteRoutes["profile"]);
            }}
          >
            Skip for now
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default QuickAccountCredentials;
